import React, { useState, useRef } from 'react';
import { Tooltip } from "@material-ui/core";
import { emojiPlugin } from "../TextInput/index";
import useOutsideAlerter from "../../../hooks/useOutsideAlerter";
import 'emoji-mart/css/emoji-mart.css';
import { 
  Container, StyledIconButton,
  StyledAttachFileIcon, StyleArrowIcon,
  StyledEmojiIcon, StyledCollapse, StyledVoiceChatIcon
} from './styles';
import { useMessageState, useTheme, useChatState, useAppState } from '../../../data';
import { MessageType } from '../../../chat-api';

export interface IconPanelProps {
  iconsColor?: string;
  iconsBackgroundHoverColor?: string | null;
  backgroundColor?: string;
}

const getType = (type: string) => {
  const isIMG = /(image)\/.*$/igm;
  const isVIDEO = /(video)\/.*$/igm;
  if(isIMG.test(type))  
    return MessageType.Image;
  else if(isVIDEO.test(type)) 
    return MessageType.Video;
  else
    return MessageType.Document;
}

const IconPanel: React.FC<IconPanelProps> = () => {
  const { getTranslation } = useAppState();
  const { iconsPanel } = useTheme().footer!;
  const attachFileRef = useRef<HTMLInputElement>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const { Picker } = emojiPlugin;
  const emojiPickerRef = useRef(null);
  const { chat, isExpanded, isMobile } = useChatState();
  const { hasVideo } = useAppState();
  const { uploadAndSend, sendMessage, setScrollBottom } = useMessageState();

  useOutsideAlerter(emojiPickerRef, setShowEmojiPicker);
  
  const handleEmojiClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setShowEmojiPicker(prev => {
      return !prev
    });
  }

  const handleCameraClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sendMessage({
      type: MessageType.VideoConference,
      body: {}
    });
    setScrollBottom(true);
  }

  const handleAttachFileClick = () => {
    attachFileRef?.current?.click();
  }

  const handleFile = (files: File[]) => {
    uploadAndSend(files[0], getType(files[0].type)!);
  }

  let icons: any[] = [
    {id: 1, icon: <StyledEmojiIcon />, tooltip: getTranslation('ICON_PANEL_EMOJI_TOOLTIP'), onClick: handleEmojiClick, ref: emojiPickerRef }
  ];

  if (chat) {
    icons.unshift({id: 0, icon: <StyledAttachFileIcon />, tooltip: getTranslation('ICON_PANEL_FILE_TOOLTIP'), onClick: handleAttachFileClick});
  }

  if (hasVideo) {
    icons.push({id: 2, icon: <StyledVoiceChatIcon />, tooltip: getTranslation('ICON_PANEL_VIDEO_TOOLTIP'), onClick: handleCameraClick});
  }

  return (
    <>
      <input 
        id="my-file-Input" 
        accept="*" 
        type="file" 
        ref={attachFileRef} 
        onChange={(e: any) => handleFile(e.target.files)}
        style={{ display: 'none' }} 
      />
      <Container styles={{...iconsPanel}} as="sdiv">
        {icons.map((icon, index) => (
          <sdiv ref={icon.ref} key={index}>
            <StyledIconButton key={icon.id} onClick={icon.onClick}>
              <Tooltip arrow title={icon.tooltip}>
                {icon.icon}
              </Tooltip>
            </StyledIconButton>
            {icon.id === 1 &&
            <StyledCollapse
              component="sdiv"
              in={showEmojiPicker}
              timeout={{ appear: 100, enter: 50, exit: 50 }}
              index={icons.findIndex(el => el.id === 1)}
              offset={isExpanded || isMobile}
            >
              <Picker 
                perline={4}
                showPreview={false}
                showSkinTones={false}
                title={getTranslation('EMOJI_PICKER_TITLE')}
                i18n={{ 
                  search: getTranslation('EMOJI_PICKER_SEARCH'), 
                  clear: getTranslation('EMOJI_PICKER_CLEAR'), // Accessible label on "clear" button
                  notfound: getTranslation('EMOJI_PICKER_NOTFOUND'),
                  skintext: getTranslation('EMOJI_PICKER_CAT_SKINTEXT'),
                  categories: { 
                    search: getTranslation('EMOJI_PICKER_CAT_SEARCH'), 
                    recent: getTranslation('EMOJI_PICKER_CAT_RECENT'),
                    people: getTranslation('EMOJI_PICKER_CAT_PEOPLE'),
                    nature: getTranslation('EMOJI_PICKER_CAT_NATURE'),
                    foods: getTranslation('EMOJI_PICKER_CAT_FOODS'),
                    activity: getTranslation('EMOJI_PICKER_CAT_ACTIVITY'),
                    places: getTranslation('EMOJI_PICKER_CAT_PLACES'),
                    objects: getTranslation('EMOJI_PICKER_CAT_OBJECTS'),
                    symbols: getTranslation('EMOJI_PICKER_CAT_SYMBOLS'),
                    flags: getTranslation('EMOJI_PICKER_CAT_FLAGS'),
                  }
                }}
              />
              <StyleArrowIcon offset={icons.findIndex(el => el.id === 1) === 0 && (isExpanded || isMobile)}/>
            </StyledCollapse>
            }
          </sdiv>
        ))}
      </Container>
    </>
  );
}

export default IconPanel;